/**
 * Canonical locale registry shared across all Joy services.
 *
 * ORDER IS SEMANTIC — do not sort alphabetically. resolveLocale picks the
 * first supported entry per base language, so the preferred regional variant
 * must come first: bare 'en' resolves to en-US only because en-US precedes
 * en-GB/en-AU/en-CA here. Same for es → es-ES and fr → fr-FR.
 */
export const SUPPORTED_LOCALES = [
  'en-US',
  'en-GB',
  'en-AU',
  'en-CA',
  'en-SG',
  'de-DE',
  'es-ES',
  'es-US',
  'fr-FR',
  'fr-CA',
  'it-IT',
  'ja-JP',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const I18N_FALLBACK_LOCALE: SupportedLocale = 'en-US';

/**
 * Explicit aliases for English variants without dedicated translation bundles.
 * Consulted BEFORE the generic resolver — encodes product-intent routing
 * (e.g. en-IE → en-GB instead of en-US which the generic sibling matcher
 * would pick as the closest English variant).
 */
export const LOCALE_TRANSLATION_MAP: Readonly<Record<string, SupportedLocale>> =
  {
    'en-IE': 'en-GB',
    'en-NZ': 'en-AU',
  };

/**
 * Locales for the `languages` field in translate.config.js.
 * Every supported locale except the base locale (en-US), which is the
 * translation SOURCE and so is never a target.
 *
 * en-SG was excluded here until 2.3.0 on the assumption that it is
 * hand-maintained as a copy of en-GB. Nothing maintained it — the bundles
 * froze at their seed values and drifted from en-GB. Translating it is
 * cheaper than curating it, and lets the model determine the actual
 * en-SG/en-GB delta rather than encoding a human's guess at it. If en-SG
 * should genuinely mirror another dialect verbatim, drop it from
 * SUPPORTED_LOCALES and add it to LOCALE_TRANSLATION_MAP instead — that is
 * the mechanism for "no bundle of its own".
 */
export const TRANSLATABLE_LOCALES = SUPPORTED_LOCALES.filter(
  (l): l is Exclude<SupportedLocale, 'en-US'> => l !== 'en-US',
);
